import moneyMask from '@eduzz/houston-forms/masks/money';
import Input from '@eduzz/houston-ui/Forms/Input';
import Select from '@eduzz/houston-ui/Forms/Select';
import Link from '@material-ui/core/Link';
import Tooltip from '@material-ui/core/Tooltip';
import CancelIcon from '@material-ui/icons/Cancel';
import HelpIcon from '@material-ui/icons/Help';
import Alert from '@material-ui/lab/Alert';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useObservable } from 'react-use-observable';
import { numberToBRL } from 'v2/helpers/money';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementService } from 'v2/services/financialStatement/statement';
import * as Yup from 'yup';

import { MIN_TRANSFER, MIN_TRANSFER_INTERNATIONAL } from './contants';
import styles from '../styles';

import type { TransferByInvoice } from '../../constants';

type selectOption = {
  label: string;
  value: string;
};

type Props = {
  loading: boolean;
  formIsValid: boolean;
  selectedBank: number;
  accountBankList: selectOption[];
  setSelectedBank: React.Dispatch<React.SetStateAction<number>>;
  valueToTransfer: string | null;
  setValueToTransfer: React.Dispatch<React.SetStateAction<string>>;
  setFormIsValid: Function;
  transferByInvoiceObj: TransferByInvoice;
  resetTransferByInvoiceObj: Function;
};

function TransferStep({
  loading,
  formIsValid,
  selectedBank,
  accountBankList,
  setSelectedBank,
  valueToTransfer,
  setValueToTransfer,
  setFormIsValid,
  transferByInvoiceObj,
  resetTransferByInvoiceObj,
}: Props) {
  const classes = styles();

  const [formMessageError, setFormMessageError] = useState(null);

  const [currentBalance] = useObservable(() => financialStatementService.getCurrentBalance(), []);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const balanceFormatted = typeof currentBalance === 'number' ? numberToBRL(currentBalance) : '-';

  const validationMinValue = user?.international_account ? MIN_TRANSFER_INTERNATIONAL : MIN_TRANSFER;
  const validationMinValueFormatted = numberToBRL(validationMinValue);
  const validationMaxValue = currentBalance;
  const validationMaxValueFormatted = balanceFormatted;

  const validateValue = async (value: string) => {
    const transferSchema = Yup.object().shape({
      valueToTransfer: Yup.number()
        .required()
        .min(validationMinValue, `Valor mínimo: ${validationMinValueFormatted}`)
        .max(validationMaxValue, `Valor máximo: ${validationMaxValueFormatted}`),
    });

    try {
      await transferSchema.validate({ valueToTransfer: value });
      setFormIsValid(true);
      setFormMessageError(null);
    } catch (err) {
      setFormIsValid(false);
      setFormMessageError(err.message);
    }
  };

  const handleInputChange = (value: string) => {
    resetTransferByInvoiceObj();
    validateValue(value);
    setValueToTransfer(value);
  };

  const handleInputReset = () => {
    setValueToTransfer('');
    validateValue('0');
  };

  const getTransferByInvoiceMessage = () => {
    const transferByInvoiceFormatted = numberToBRL(transferByInvoiceObj.approx_amount);

    if (transferByInvoiceObj.approx_amount < validationMinValue) {
      return (
        <>
          <div>{`O valor mínimo para saque em nossa plataforma é de ${validationMinValueFormatted} e a soma de faturas com valor mais próximo ao solicitado é de ${transferByInvoiceFormatted}.`}</div>
          <div>Tente novamente com um valor acima disso e dará tudo certo</div>
        </>
      );
    }

    return `A soma das faturas com valor mais próximo ao solicitado é de ${transferByInvoiceFormatted}. Clique em "Transferir valor" para prosseguir com o valor apurado.`;
  };

  useEffect(() => setValueToTransfer(String(currentBalance)), [currentBalance, setValueToTransfer]);

  return (
    <>
      <p className={classes.subtitle}>Informe o valor que deseja transferir para sua conta bancária.</p>

      <div className={classes.valueAvailable}>
        <span>Disponível para transferência:</span>
        <strong>{validationMaxValueFormatted}</strong>
      </div>

      <div className={classes.selectValueAvailable}>
        <span>
          Valor que deseja transferir:
          <Tooltip title='Será debitado o valor de R$ 9,00 do valor transferido devido encargos bancários.' placement='top'>
            <HelpIcon className={classes.tooltipIcon} />
          </Tooltip>
        </span>
        <Input
          className={classes.inputValue}
          name='valueToTransfer'
          placeholder='R$ 0,00'
          disabled={typeof valueToTransfer === null || loading}
          value={valueToTransfer}
          onChange={handleInputChange}
          mask={moneyMask}
          errorMessage={!formIsValid && formMessageError}
          endAdornment={<CancelIcon className={classes.clearValue} onClick={handleInputReset} />}
        />
      </div>

      {!!transferByInvoiceObj && <Alert severity='warning'>{getTransferByInvoiceMessage()}</Alert>}

      <div className={classes.selectAccount}>
        {!!accountBankList && (
          <>
            <label className={classes.selectLabel}>Conta bancária</label>
            <Select name='accountBanklist' value={selectedBank} onChange={setSelectedBank} disabled={loading || !accountBankList} fullWidth>
              {accountBankList?.map((option, index) => (
                <Select.Option key={`${option.value}-${index}`} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </>
        )}
        {!accountBankList && (
          <Alert severity='warning'>
            Falta pouco! Como você não tem nenhuma conta bancária ativa no momento,{' '}
            <Link component={RouterLink} to='/producer/profile/bankdata'>
              <strong>clique aqui e cadastre agora!</strong>
            </Link>
          </Alert>
        )}
      </div>
    </>
  );
}

export default TransferStep;
